/**
 * PodcastSheet — «Диалог»: реплики разными голосами в одном файле
 * (ElevenLabs text-to-dialogue, бэк — /api/v1/voice/dialogue).
 *
 * Почему отдельным окном, а не полем виджета: у диалога не один текст, а
 * список реплик, у каждой свой голос. В одну строку промпта это не ложится.
 * Приём тот же, что у «Сгенерировать текст песни» — пилюля открывает своё окно
 * и само действие живёт там.
 *
 * Ограничения — из API ElevenLabs: до 2000 символов на все реплики; реплик у
 * нас до 10 (у ElevenLabs до 10 разных голосов на запрос — больше не бывает).
 * Голоса — библиотека ElevenLabs: сверху язык и сценарий (как в окне выбора
 * голоса), у реплики — список голосов этого раздела и ▶ рядом. Выбранный голос
 * реплики держится и после смены фильтра.
 * Синхронно: mp3 приходит в ответе, отдельного ожидания нет.
 */
import { useState } from 'react'
import { useCreditsGuard } from '../../../hooks/useCreditsGuard'
import CreditsShortNote from '../../../components/CreditsShortNote'
import Sheet from '../../../components/Sheet'
import TokenPrice from '../../../components/TokenPrice'
import { Icon } from '../../../../public/icons/Icon.tsx'
import { showToast } from '../../../components/Toast'
import { generateDialogue } from '../../../api/voice'
import { useVoiceFilters, useVoiceLibrary, type TtsVoice } from '../utils/ttsVoices'
import { useVoicePreview } from '../hooks/useVoicePreview'
import { VoiceFilters } from './VoiceLibraryPanel'
import type { Model } from '../../../data/models'

const MAX_LINES = 10
const MAX_TOTAL_CHARS = 2000
/** Цена, если тариф ещё не пришёл с бэка. Совпадает с фолбэком сервера
 *  (elevenlabs_tts.policy_credits: 2000 символов × $0.08/1000 → 116). */
const FALLBACK_PRICE = 116

interface Line {
	/** null — голос ещё не выбран: берём из списка раздела, когда он загрузится. */
	voice: TtsVoice | null
	text: string
}

interface Props {
	open: boolean
	onClose: () => void
	model: Model | null
	/** Готовый диалог — родитель обновляет список озвучек. */
	onCreated?: () => void
}

/** Голос реплики: выбранный или — пока не выбран — чередуем первые голоса
 *  раздела (диалог двух людей без лишних кликов). */
function lineVoice(voices: TtsVoice[], line: Line, idx: number): TtsVoice | null {
	if (line.voice) return line.voice
	return voices[idx % Math.min(voices.length, 2)] || null
}

export default function PodcastSheet({ open, onClose, model, onCreated }: Props) {
	const guard = useCreditsGuard()
	// Стартуем с двух реплик — это и есть смысл диалога; пустой список
	// заставлял бы догадываться, что тут делать.
	const [lines, setLines] = useState<Line[]>([
		{ voice: null, text: '' },
		{ voice: null, text: '' },
	])
	const [title, setTitle] = useState('')
	const [busy, setBusy] = useState(false)
	const filters = useVoiceFilters()
	const { voices, loading, error, hasMore, loadMore } = useVoiceLibrary(filters.language, filters.useCase)
	// Тот же хук, что в пикере голоса: один источник логики проигрывания.
	const { playing, toggle, stop } = useVoicePreview()

	const price =
		model?.variants?.find((v) => v.key === 'tts_dialogue')?.credits_price ?? FALLBACK_PRICE
	const total = lines.reduce((n, l) => n + l.text.length, 0)
	const filled = lines.filter((l) => l.text.trim())
	const overLimit = total > MAX_TOTAL_CHARS

	const setLine = (idx: number, patch: Partial<Line>) =>
		setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)))

	// Лимит считаем ВНУТРИ обновления состояния, от `prev`: снаружи `lines` —
	// значение из замыкания текущего рендера, и быстрые клики проскакивали бы
	// мимо потолка (живая проверка старого окна дала 13 реплик при потолке 10).
	const addLine = () => {
		setLines((prev) => (prev.length >= MAX_LINES ? prev : [...prev, { voice: null, text: '' }]))
	}

	const removeLine = (idx: number) =>
		setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)))

	const create = async () => {
		const picked = lines.map((l, i) => ({ text: l.text.trim(), voice: lineVoice(voices, l, i) })).filter((l) => l.text)
		if (!picked.length || busy || overLimit || picked.some((l) => !l.voice)) return
		stop() // не оставляем превью играть поверх сборки
		setBusy(true)
		try {
			const res = await generateDialogue({
				lines: picked.map((l) => ({ text: l.text, voice_id: l.voice!.id })),
				model: model?.id || '',
				title: title.trim() || 'Диалог',
			})
			if (!res.ok) {
				showToast(res.error || 'Не удалось собрать диалог', 'error')
				return
			}
			// Диалог сохраняется как озвучка (operation='tts') — говорим именно то
			// место, где он появится.
			showToast('Диалог готов — он в ваших озвучках', 'success')
			onCreated?.()
			onClose()
		} finally {
			setBusy(false)
		}
	}

	return (
		<Sheet
			open={open}
			onClose={() => { stop(); onClose() }}
			variant="center"
			title="Диалог"
			className="podcast-sheet"
		>
			<div className="podcast-sheet__body">
				<p className="podcast-sheet__hint">
					Реплики озвучиваются по очереди разными голосами и склеиваются в один файл.
					До {MAX_LINES} реплик и {MAX_TOTAL_CHARS} символов на все вместе.
				</p>

				<input
					className="podcast-sheet__title"
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					placeholder="Название (необязательно)"
					maxLength={200}
				/>

				<VoiceFilters filters={filters} disabled={busy} />
				{!voices.length && (
					<p className="podcast-sheet__hint">
						{loading ? 'Загружаем голоса…' : error || 'В этом разделе пока нет голосов на выбранном языке'}
					</p>
				)}
				{hasMore && (
					<button type="button" className="podcast-sheet__more" onClick={loadMore} disabled={loading || busy}>
						{loading ? 'Загружаем…' : 'Ещё голоса в списках реплик'}
					</button>
				)}

				<div className="podcast-sheet__lines">
					{lines.map((line, idx) => {
						const voice = lineVoice(voices, line, idx)
						const isPlaying = !!voice && playing === voice.id
						// Выбранный голос мог прийти из другого раздела — держим его в списке.
						const options = voice && !voices.some((v) => v.id === voice.id) ? [voice, ...voices] : voices
						return (
							<div key={idx} className="podcast-sheet__line">
								<div className="podcast-sheet__line-head">
									<span className="podcast-sheet__line-num">Реплика {idx + 1}</span>
									<select
										className="podcast-sheet__voice-select"
										value={voice?.id || ''}
										onChange={(e) => setLine(idx, { voice: options.find((v) => v.id === e.target.value) ?? null })}
										disabled={busy || !options.length}
										aria-label={`Голос реплики ${idx + 1}`}
									>
										{options.map((v) => (
											<option key={v.id} value={v.id}>
												{v.gender === 'm' ? '♂ ' : v.gender === 'f' ? '♀ ' : ''}
												{v.label}
												{v.hint ? ` — ${v.hint}` : ''}
											</option>
										))}
									</select>
									<button
										type="button"
										className={`podcast-sheet__play${isPlaying ? ' is-playing' : ''}`}
										onClick={() => voice && toggle(voice)}
										disabled={!voice?.sample}
										aria-label={voice ? `${isPlaying ? 'Остановить' : 'Послушать'} голос ${voice.label}` : 'Послушать голос'}
									>
										<span aria-hidden="true">{isPlaying ? '■' : '▶'}</span>
									</button>
									{lines.length > 1 && (
										<button
											type="button"
											className="podcast-sheet__remove"
											onClick={() => removeLine(idx)}
											disabled={busy}
											aria-label={`Убрать реплику ${idx + 1}`}
										>
											<Icon id="gen-close" size={13} />
										</button>
									)}
								</div>
								<textarea
									className="podcast-sheet__text"
									value={line.text}
									onChange={(e) => setLine(idx, { text: e.target.value.slice(0, MAX_TOTAL_CHARS) })}
									placeholder={idx === 0 ? 'Привет! Сегодня разбираем…' : 'Ответная реплика…'}
									rows={2}
									disabled={busy}
								/>
							</div>
						)
					})}
				</div>

				<span className={`podcast-sheet__count${overLimit ? ' is-over' : ''}`}>
					{total} / {MAX_TOTAL_CHARS}
				</span>

				<div className="podcast-sheet__actions">
					<button
						type="button"
						className="suno-sheet-btn suno-sheet-btn--soft suno-sheet-btn--compact"
						onClick={addLine}
						disabled={busy || lines.length >= MAX_LINES}
					>
						<Icon id="gen-add" size={13} />
						Добавить реплику
					</button>
					<button
						type="button"
						className="suno-sheet-btn suno-sheet-btn--primary"
						onClick={() => void create()}
						disabled={busy || !filled.length || overLimit || (!voices.length && filled.some((l) => !l.voice)) || guard.shortFor(price)}
						title={guard.shortFor(price) ? guard.titleFor(price) : undefined}
					>
						{busy ? 'Собираем…' : 'Создать'}
						{!busy && (
							<span className="podcast-sheet__price">
								<TokenPrice value={price} iconSize={11} />
							</span>
						)}
					</button>
					{guard.shortFor(price) && <CreditsShortNote price={price} />}
				</div>
			</div>
		</Sheet>
	)
}
